import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import vneidSdkService from '../../../services/vneidSdkService';
import vneidAccountService from '../../../services/vneidAccountService';

export default function AssignSdkModal({ open, onClose, onConfirm, account, loading }) {
    const { t } = useTranslation();
    const [sdkList, setSdkList] = useState([]);
    const [selectedSdkIds, setSelectedSdkIds] = useState([]);
    const [loadingSdks, setLoadingSdks] = useState(false);
    const [error, setError] = useState(null);

    // Fetch SDK list and assigned SDKs when modal opens
    useEffect(() => {
        if (open && account) {
            fetchData();
        }
    }, [open, account]);

    const fetchData = async () => {
        setLoadingSdks(true);
        setError(null);
        try {
            // First: Get assigned SDKs for the account
            const assignedResponse = await vneidAccountService.getAssignedSdks(account.id);
            const assignedSdks = assignedResponse.data || assignedResponse || [];
            // Use vNeIDSDKId from assigned SDKs response
            const assignedIds = assignedSdks.map((sdk) => Number(sdk.vNeIDSDKId));
            setSelectedSdkIds(assignedIds);

            // Then: Get all available SDKs
            const sdkResponse = await vneidSdkService.getSdkList({ pageSize: 100 });
            setSdkList(sdkResponse.data || []);
        } catch (err) {
            setError(err.message || 'Failed to load SDK list');
            setSdkList([]);
            setSelectedSdkIds([]);
        } finally {
            setLoadingSdks(false);
        }
    };

    const handleToggleSdk = (sdkId) => {
        const numId = Number(sdkId);
        setSelectedSdkIds((prev) => {
            if (prev.includes(numId)) {
                return prev.filter((id) => id !== numId);
            }
            return [...prev, numId];
        });
    };

    const handleSelectAll = () => {
        if (selectedSdkIds.length === sdkList.length) {
            setSelectedSdkIds([]);
        } else {
            setSelectedSdkIds(sdkList.map((sdk) => Number(sdk.id)));
        }
    };

    const handleConfirm = () => {
        if (account) {
            onConfirm({
                accountId: account.id,
                sdkIds: selectedSdkIds,
            });
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 1:
                return 'success';
            case 2:
                return 'warning';
            case 0:
            default:
                return 'default';
        }
    };

    const getStatusName = (status) => {
        switch (status) {
            case 1:
                return t('vneid.sdk.status.active');
            case 2:
                return t('vneid.sdk.status.deprecated');
            case 0:
            default:
                return t('vneid.sdk.status.draft');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('vneid.accounts.assignSdkDialog.title')}
            </DialogTitle>
            <DialogContent>
                {account && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('vneid.accounts.assignSdkDialog.account')}: <strong>{account.accountName}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('vneid.accounts.table.unitName')}: <strong>{account.unitName}</strong>
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loadingSdks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sdkList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                        {t('vneid.accounts.assignSdkDialog.noSdkAvailable')}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox
                                checked={selectedSdkIds.length === sdkList.length && sdkList.length > 0}
                                indeterminate={selectedSdkIds.length > 0 && selectedSdkIds.length < sdkList.length}
                                onChange={handleSelectAll}
                                id="selectAllSdks"
                                name="selectAllSdks"
                                data-testid="assign-sdk-modal-checkbox-selectAll"
                            />
                            <Typography variant="body2">
                                {t('vneid.accounts.assignSdkDialog.selectAll')} ({selectedSdkIds.length}/{sdkList.length})
                            </Typography>
                        </Box>
                        <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            {sdkList.map((sdk) => (
                                <ListItem
                                    key={sdk.id}
                                    dense
                                    button
                                    onClick={() => handleToggleSdk(sdk.id)}
                                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <Checkbox
                                            edge="start"
                                            checked={selectedSdkIds.includes(Number(sdk.id))}
                                            tabIndex={-1}
                                            disableRipple
                                            id={`sdk-${sdk.id}`}
                                            name={`sdk-${sdk.id}`}
                                            data-testid={`assign-sdk-modal-checkbox-sdk-${sdk.id}`}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {sdk.version}
                                                </Typography>
                                                <Chip
                                                    label={getStatusName(sdk.status)}
                                                    color={getStatusColor(sdk.status)}
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        secondary={sdk.description || sdk.fileName}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    id="cancelAssignSdk"
                    name="cancelAssignSdk"
                    data-testid="assign-sdk-modal-button-cancel"
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={loading}
                    id="confirmAssignSdk"
                    name="confirmAssignSdk"
                    data-testid="assign-sdk-modal-button-confirm"
                >
                    {loading ? <CircularProgress size={20} /> : t('vneid.accounts.assignSdkDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
